import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Plus, Trash2 } from 'lucide-react';
import { AnnotationToolbar } from './AnnotationToolbar';
import { AnnotationCanvas, DEFAULT_TOOL_STYLE } from './AnnotationCanvas';
import { AnnotationPropertiesPanel } from './AnnotationPropertiesPanel';

export function ImageGrid({
  images, onSetImage, onRemoveImage, onAddImageSlot, onRemoveImageSlot, onUpdateLabel,
  onAddAnnotation, onRemoveAnnotation, onUpdateAnnotation,
  onAddTextBox, onRemoveTextBox, onUpdateTextBox,
  onAddHighlight, onRemoveHighlight, onUpdateHighlight,
}) {
  const [activeTool, setActiveTool] = useState('select');
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [activeToolStyle, setActiveToolStyle] = useState({ ...DEFAULT_TOOL_STYLE });

  const highlightColor = activeToolStyle.strokeColor;
  const highlightOpacity = activeToolStyle.opacity;
  const highlightStrokeWidth = activeToolStyle.lineThickness * 7.5;

  const handleSelect = (imageIndex, id, type) => {
    setSelectedId(id); setSelectedType(type); setActiveImageIndex(id ? imageIndex : null);
  };

  const handleDrop = (index, e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) onSetImage(index, file);
  };

  const handleFileSelect = (index, e) => {
    const file = e.target.files?.[0];
    if (file) onSetImage(index, file);
  };

  const selectedAnnotation = selectedId && selectedType === 'annotation' && activeImageIndex !== null ? images[activeImageIndex]?.annotations.find(a => a.id === selectedId) : null;
  const selectedTextBox = selectedId && selectedType === 'textbox' && activeImageIndex !== null ? (images[activeImageIndex]?.textBoxes || []).find(t => t.id === selectedId) : null;
  const selectedHighlight = selectedId && selectedType === 'highlight' && activeImageIndex !== null ? (images[activeImageIndex]?.highlights || []).find(h => h.id === selectedId) : null;

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-foreground">CAD Views</h2>
        <Button variant="outline" size="sm" onClick={onAddImageSlot} className="h-7 text-xs" data-testid="cad-add-view">
          <Plus className="h-3 w-3 mr-1" /> Add View
        </Button>
      </div>
      <div className="flex gap-3">
        <div className="sticky top-0 self-start z-10">
          <AnnotationToolbar activeTool={activeTool} onToolChange={setActiveTool} />
        </div>
        <div className="flex-1 flex flex-col gap-4">
          {images.map((img, i) => (
            <div key={i} className="border border-border rounded-lg overflow-hidden bg-muted/20" style={{ minHeight: '70vh' }}>
              <div className="flex items-center justify-between px-2 py-1 bg-muted/40 border-b border-border">
                <input className="text-xs font-medium bg-transparent border-none outline-none text-foreground w-24" value={img.label} onChange={e => onUpdateLabel(i, e.target.value)} />
                <div className="flex gap-0.5">
                  {img.url && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => onRemoveImage(i)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                  {images.length > 1 && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => onRemoveImageSlot(i)} title="Remove this view">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
              {img.url ? (
                <AnnotationCanvas
                  image={img} imageIndex={i} activeTool={activeTool}
                  selectedId={activeImageIndex === i ? selectedId : null}
                  onSelect={(id, type) => handleSelect(i, id, type)}
                  onAddAnnotation={ann => onAddAnnotation(i, ann)}
                  onUpdateAnnotation={(id, updates) => onUpdateAnnotation(i, id, updates)}
                  onRemoveAnnotation={id => onRemoveAnnotation(i, id)}
                  onAddTextBox={tb => onAddTextBox(i, tb)}
                  onUpdateTextBox={(id, updates) => onUpdateTextBox?.(i, id, updates)}
                  onRemoveTextBox={id => onRemoveTextBox(i, id)}
                  onAddHighlight={hl => onAddHighlight?.(i, hl)}
                  onRemoveHighlight={id => onRemoveHighlight?.(i, id)}
                  highlightColor={highlightColor} highlightOpacity={highlightOpacity} highlightStrokeWidth={highlightStrokeWidth}
                  activeToolStyle={activeToolStyle}
                />
              ) : (
                <label className="aspect-square flex flex-col items-center justify-center cursor-pointer hover:bg-muted/40 transition-colors" onDragOver={e => e.preventDefault()} onDrop={e => handleDrop(i, e)}>
                  <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                  <span className="text-xs text-muted-foreground">Drop or click to upload</span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleFileSelect(i, e)} />
                </label>
              )}
            </div>
          ))}
        </div>
        <div className="w-44 border border-border rounded-lg bg-card/50 flex-shrink-0">
          <div className="text-[10px] font-semibold text-muted-foreground px-3 py-2 border-b border-border">PROPERTIES</div>
          <AnnotationPropertiesPanel
            selectedAnnotation={selectedAnnotation} selectedTextBox={selectedTextBox} selectedHighlight={selectedHighlight}
            activeTool={activeTool} activeToolStyle={activeToolStyle} onUpdateActiveToolStyle={setActiveToolStyle}
            onUpdateAnnotationStyle={(id, style) => { if (activeImageIndex !== null) { const ann = images[activeImageIndex].annotations.find(a => a.id === id); if (ann) { const currentStyle = ann.style || { lineColor: '#000000', lineWidth: 0.4, fontSize: 2.8, textColor: '#000000' }; onUpdateAnnotation(activeImageIndex, id, { style: { ...currentStyle, ...style } }); } } }}
            onUpdateAnnotationValue={(id, value) => { if (activeImageIndex !== null) onUpdateAnnotation(activeImageIndex, id, { value }); }}
            onDeleteAnnotation={id => { if (activeImageIndex !== null) { onRemoveAnnotation(activeImageIndex, id); setSelectedId(null); } }}
            onUpdateTextBoxStyle={(id, style) => { if (activeImageIndex !== null) { const tb = (images[activeImageIndex].textBoxes || []).find(t => t.id === id); if (tb) { const currentStyle = tb.style || { bold: true, fontSize: 2.8, color: '#000000' }; onUpdateTextBox?.(activeImageIndex, id, { style: { ...currentStyle, ...style } }); } } }}
            onUpdateTextBoxValue={(id, value) => { if (activeImageIndex !== null) onUpdateTextBox?.(activeImageIndex, id, { value }); }}
            onDeleteTextBox={id => { if (activeImageIndex !== null) { onRemoveTextBox(activeImageIndex, id); setSelectedId(null); } }}
            onUpdateHighlight={(id, updates) => { if (activeImageIndex !== null) onUpdateHighlight?.(activeImageIndex, id, updates); }}
            onDeleteHighlight={id => { if (activeImageIndex !== null) { onRemoveHighlight?.(activeImageIndex, id); setSelectedId(null); } }}
          />
        </div>
      </div>
    </div>
  );
}
